import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUserSettings, updateUserSettings } from '../lib/api';

const SettingsContext = createContext();

const defaultSettings = {
  // Display/Layout
  mobileMode: false,
  compactMode: false,
  
  // Colors
  primaryColor: '#00305E',
  accentColor: '#A62E1D',
  
  // Theme
  darkMode: false,
  highContrast: false,
  
  // Reports
  defaultDateRange: 30, // days
  
  // Notifications (per-user bell preferences)
  notifyAttendance: true,
  notifyCalendar: true,
  notifyCommFollowup: true,
  notifyCommTask: true,
  notifyLicense: true,

  // Email reminders (per-user)
  assessmentReminderEmail: true,
  
  // Dashboard Widgets
  showStudentCount: true,
  showAttendanceToday: true,
  showRoster: true,
  showPlanReviews: true,
  showQuickActions: true,
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    // Initially load from localStorage for fast startup
    const saved = localStorage.getItem('tms_settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });
  
  const [synced, setSynced] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Load settings from backend when user logs in
  const loadFromBackend = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await getUserSettings();
      if (res.data && Object.keys(res.data).length > 0) {
        // Merge backend settings with defaults
        const merged = { ...defaultSettings, ...res.data };
        setSettings(merged);
        localStorage.setItem('tms_settings', JSON.stringify(merged));
      }
      setSynced(true);
    } catch (err) {
      // If API fails, use local settings
      console.log('Using local settings');
      setSynced(true);
    }
  }, []);

  // Save settings to backend (debounced)
  const saveToBackend = useCallback(async (newSettings) => {
    const token = localStorage.getItem('token');
    if (!token || syncing) return;
    
    setSyncing(true);
    try {
      await updateUserSettings(newSettings);
    } catch (err) {
      console.error('Failed to sync settings to backend:', err);
    } finally {
      setSyncing(false);
    }
  }, [syncing]);

  // Load from backend on mount if logged in
  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  // Apply CSS variables when settings change
  useEffect(() => {
    const root = document.documentElement;
    
    // Primary and accent colors
    root.style.setProperty('--primary-color', settings.primaryColor);
    root.style.setProperty('--accent-color', settings.accentColor);
    
    // Convert hex to RGB for opacity variants
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
    };
    root.style.setProperty('--primary-rgb', hexToRgb(settings.primaryColor));
    root.style.setProperty('--accent-rgb', hexToRgb(settings.accentColor));
    
    // Dark mode
    if (settings.darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // High contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Compact mode
    if (settings.compactMode) {
      root.classList.add('compact');
    } else {
      root.classList.remove('compact');
    }
    
    // Mobile mode
    if (settings.mobileMode) {
      root.classList.add('mobile-optimized');
    } else {
      root.classList.remove('mobile-optimized');
    }
    
    // Save to localStorage (always, for fast load)
    localStorage.setItem('tms_settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    // Save to backend after updating
    saveToBackend(newSettings);
  };

  const updateSettings = (newSettingsPartial) => {
    const newSettings = { ...settings, ...newSettingsPartial };
    setSettings(newSettings);
    // Save to backend after updating
    saveToBackend(newSettings);
  };

  const resetSettings = async () => {
    setSettings(defaultSettings);
    localStorage.removeItem('tms_settings');
    // Also clear from backend
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await updateUserSettings(defaultSettings);
      } catch (err) {
        console.error('Failed to reset settings on backend:', err);
      }
    }
  };

  // Function to reload settings from backend (useful after login)
  const refreshFromBackend = () => {
    setSynced(false);
    loadFromBackend();
  };

  return (
    <SettingsContext.Provider value={{ 
      settings, 
      updateSetting, 
      updateSettings, 
      resetSettings, 
      defaultSettings,
      synced,
      syncing,
      refreshFromBackend
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
